import { css } from '@emotion/react';
import { isEqual, omit } from 'lodash-es';
import { PaymentPlanDetailsResponse, PaymentPlanHistoryItem, PaymentPlanStatus } from '@frontend/api-payment-plans';
import { UsersApi } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Modal, ModalControlTriggerProps, Text, TextLink, useModalControl } from '@frontend/design-system';
import { isoDateToDisplayAbbrString } from '../../../utils/date-helpers';
import { PlanChangesModal } from '../../PaymentPlanModals/PlanChangesModal';

type DataWithoutStatus = Omit<Partial<PaymentPlanDetailsResponse>, 'schedule'> & {
  schedule: Partial<PaymentPlanDetailsResponse['schedule']>;
};

const getDataWithoutStatus = (data: PaymentPlanDetailsResponse | undefined) => {
  if (!data) return {};

  const newData: DataWithoutStatus = omit(data, [
    'status',
    'updatedAt',
    'createdAt',
    'confirmationDetails',
    'confirmationStatus',
    'isConfirmationLinkExpired',
  ]);
  newData.schedule = omit(newData.schedule, ['startAt', 'finalPaymentAt']);

  return newData;
};

export const UpdateCustomRow = ({ data }: { data: PaymentPlanHistoryItem }) => {
  const { t } = useTranslation('payments');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });
  const { modalProps: viewChangesModalProps, triggerProps: viewChangesTriggerProps } = useModalControl();

  const fromData = data.plan?.from;
  const toData = data.plan?.to;

  const hasOnlyStatusChanged = isEqual(getDataWithoutStatus(fromData), getDataWithoutStatus(toData));

  const { data: userData } = useQuery({
    queryKey: ['user', toData?.userId],
    queryFn: () => UsersApi.list.getUserProfileByUserID(toData?.userId ?? ''),
    enabled: !!toData?.userId,
    // Swallow the error if user cannot be found
  });

  return (
    <>
      <div
        css={css`
          display: flex;
          width: 100%;
          justify-content: space-between;
          ${isMobile &&
          `
            padding: ${theme.spacing(1)};
            background: ${theme.colors.neutral5};
            border-top: 1px solid ${theme.colors.neutral20}
          `}
        `}
      >
        <Text>{isoDateToDisplayAbbrString(data.occurredAt)}</Text>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          {toData?.status === PaymentPlanStatus.Paused ? (
            <StatusModal
              title={hasOnlyStatusChanged ? t('Payment plan was paused') : t('Payment plan was edited and paused')}
              viewChangesTriggerProps={viewChangesTriggerProps}
            />
          ) : toData?.status === PaymentPlanStatus.Canceled ? (
            <Text>{t('Payment plan was canceled')}</Text>
          ) : fromData?.status === PaymentPlanStatus.Paused ? (
            <Text>{t('Payment plan was resumed')}</Text>
          ) : (
            <StatusModal title={t('Payment plan was edited')} viewChangesTriggerProps={viewChangesTriggerProps} />
          )}
        </div>
      </div>
      <Modal {...viewChangesModalProps} maxWidth={600}>
        <PlanChangesModal
          fromData={fromData}
          toData={toData}
          changeReason={toData?.notes}
          onClose={viewChangesModalProps.onClose}
          viewType={data.plan?.to?.status === PaymentPlanStatus.Paused ? 'paused' : 'view'}
          updatedBy={userData ? `${userData.FirstName} ${userData.LastName}` : t('Unknown User')}
        />
      </Modal>
    </>
  );
};

const StatusModal = ({
  title,
  viewChangesTriggerProps,
}: {
  title: string;
  viewChangesTriggerProps: ModalControlTriggerProps;
}) => {
  const { t } = useTranslation('payments');
  return (
    <div
      css={css`
        display: flex;
        gap: ${theme.spacing(2)};
      `}
    >
      <Text>{title}</Text>
      <TextLink data-trackingid='pay-portal-paymentplans-customrow-view' {...viewChangesTriggerProps}>
        {t('View Details')}
      </TextLink>
    </div>
  );
};
